import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ClipboardList, ShoppingCart } from "lucide-react";
import { useCartContext } from "../../../components/providers/CartProvider.jsx";
import getMediaUrl from "../../../components/tools/utils.jsx";

const fmtCurrency = (value, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "EUR" }).format(Number(value || 0));

export default function PurchaseLinkPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { setCartItems, fetchCart } = useCartContext();
    const [proposal, setProposal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchProposal = async () => {
            setLoading(true);
            setError(null);
            try {
                const { data } = await axios.get(`/purchase-links/${token}/`);
                if (isMounted) setProposal(data);
            } catch (err) {
                console.error("Error loading purchase proposal:", err);
                if (isMounted) {
                    setError(err.response?.data?.detail || "Este enlace de compra no está disponible.");
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchProposal();
        return () => {
            isMounted = false;
        };
    }, [token]);

    const handleAddToCart = async () => {
        setAdding(true);
        setError(null);
        try {
            const { data } = await axios.post(`/purchase-links/${token}/add-to-cart/`, null, {
                withCredentials: true,
            });
            setCartItems(data.items || []);
            await fetchCart();
            navigate("/cart");
        } catch (err) {
            console.error("Error adding approved proposal to cart:", err);
            setError(err.response?.data?.detail || "No se pudo añadir la propuesta al carrito.");
        } finally {
            setAdding(false);
        }
    };

    if (loading) {
        return (
            <div className="h-full w-full flex items-center justify-center px-4">
                <div className="flex flex-col items-center gap-3 text-base-content/70">
                    <span className="loading loading-spinner loading-lg text-primary" />
                    <p>Cargando propuesta</p>
                </div>
            </div>
        );
    }

    if (error && !proposal) {
        return (
            <div className="h-full w-full flex items-center justify-center px-4">
                <div className="glassCard rounded-lg p-6 max-w-md text-center">
                    <ClipboardList className="w-10 h-10 mx-auto text-error mb-3" />
                    <h1 className="text-xl font-semibold mb-2">Enlace no disponible</h1>
                    <p className="text-sm text-base-content/70">{error}</p>
                    <button className="btn btn-primary btn-sm mt-5" onClick={() => navigate("/store")}>
                        Ir a la tienda
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full w-full overflow-y-auto">
            <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
                <div className="flex items-center gap-2 text-sm text-success mb-4">
                    <CheckCircle2 className="w-4 h-4" />
                    Propuesta aprobada
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
                    <section className="glassCard rounded-lg p-5 md:p-6">
                        <div className="flex flex-col md:flex-row gap-5">
                            <div className="w-full md:w-44 aspect-square rounded-lg bg-base-200 border border-base-300 overflow-hidden flex items-center justify-center">
                                {proposal.thumbnail ? (
                                    <img
                                        src={getMediaUrl(proposal.thumbnail)}
                                        alt={proposal.design_name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <ClipboardList className="w-12 h-12 text-base-content/30" />
                                )}
                            </div>

                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-base-content/50 mb-1">{proposal.reference}</p>
                                <h1 className="text-2xl font-bold mb-3">{proposal.design_name}</h1>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div>
                                        <span className="block text-xs text-base-content/50">Cantidad</span>
                                        {proposal.quantity} uds.
                                    </div>
                                    <div>
                                        <span className="block text-xs text-base-content/50">Material</span>
                                        {proposal.material}
                                    </div>
                                    <div>
                                        <span className="block text-xs text-base-content/50">Color</span>
                                        {proposal.color}
                                    </div>
                                    <div>
                                        <span className="block text-xs text-base-content/50">Acabado</span>
                                        {proposal.finish || "Estándar"}
                                    </div>
                                </div>

                                {proposal.proposal_message && (
                                    <p className="mt-5 text-sm whitespace-pre-wrap text-base-content/70">
                                        {proposal.proposal_message}
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>

                    <aside className="glassCard rounded-lg p-5 flex flex-col gap-4">
                        <div>
                            <span className="text-xs text-base-content/50">Precio final aprobado</span>
                            <p className="text-3xl font-bold">
                                {fmtCurrency(proposal.approved_final_price, proposal.approved_currency)}
                            </p>
                        </div>

                        {proposal.proposal_valid_until && (
                            <p className="text-xs text-base-content/60">
                                Válida hasta {new Date(proposal.proposal_valid_until).toLocaleDateString("es-ES")}
                            </p>
                        )}

                        {error && (
                            <div role="alert" className="alert alert-error text-sm">
                                <span>{error}</span>
                            </div>
                        )}

                        <button
                            className="btn btn-primary w-full gap-2"
                            onClick={handleAddToCart}
                            disabled={adding}
                        >
                            {adding ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <ShoppingCart className="w-4 h-4" />
                            )}
                            Añadir al carrito
                        </button>

                        <p className="text-xs text-base-content/50">
                            Este enlace solo añade esta propuesta aprobada. No permite editar el diseño ni acceder a otros modelos.
                        </p>
                    </aside>
                </div>
            </div>
        </div>
    );
}
